import { getSupabase, getCustomerSessionToken } from './supabase';
import { CustomerOrder, CustomerProfile } from '../types';
import { getReferralRewardSettings } from './supabaseReferralSettings';
import { getApiUrl } from './api';

export interface LoyaltySettings {
  id: string;
  isActive: boolean;
  earningAmountPerPoint: number;
  calculationBasis: 'SUBTOTAL' | 'TOTAL';
  expirationMode: 'NEVER' | 'DAYS';
  expirationDays: number;
}

export interface LoyaltyReward {
  id: string;
  name: string;
  description: string;
  pointsRequired: number;
  rewardType: 'DISCOUNT_PERCENT' | 'DISCOUNT_NOMINAL' | 'FREE_ITEM';
  rewardValue: number;
  isActive: boolean;
  redeemLimit?: number | null;
  createdAt?: string;
}

export interface PointTransaction {
  id: string;
  customerId: string;
  customerName?: string;
  customerPhone?: string;
  transactionType: 'EARN' | 'REDEEM' | 'MANUAL_ADD' | 'MANUAL_SUB' | 'EXPIRED';
  points: number;
  balanceBefore: number;
  balanceAfter: number;
  referenceOrderId?: string | null;
  referenceRewardId?: string | null;
  reason: string;
  adminUsername?: string | null;
  createdAt: string;
}

export interface RewardRedemption {
  id: string;
  customerId: string;
  customerName?: string;
  customerPhone?: string;
  rewardId: string;
  rewardName: string;
  rewardType: string;
  rewardValue: number;
  pointsSpent: number;
  pointsBalanceAfter?: number;
  status: 'ACTIVE' | 'USED' | 'EXPIRED';
  voucherCode?: string;
  referenceOrderId?: string | null;
  usedAtOutletId?: string | null;
  usedByAdmin?: string | null;
  createdAt: string;
  expiresAt?: string;
  usedAt?: string | null;
}

export interface CustomerLoyaltyData {
  pointsBalance: number;
  totalPointsEarned: number;
  totalPointsRedeemed: number;
}

interface LoyaltyRegistry {
  balances: Record<string, CustomerLoyaltyData>;
  transactions: PointTransaction[];
  redemptions: RewardRedemption[];
}

const DEFAULT_SETTINGS: LoyaltySettings = {
  id: 'default',
  isActive: true,
  earningAmountPerPoint: 10000,
  calculationBasis: 'SUBTOTAL',
  expirationMode: 'NEVER',
  expirationDays: 365,
};

const DEFAULT_REWARDS: LoyaltyReward[] = [
  {
    id: 'rwd-1',
    name: 'Potongan Rp5.000',
    description: 'Diskon langsung Rp5.000 untuk transaksi berikutnya.',
    pointsRequired: 15,
    rewardType: 'DISCOUNT_NOMINAL',
    rewardValue: 5000,
    isActive: true,
  },
  {
    id: 'rwd-2',
    name: 'Potongan Rp10.000',
    description: 'Diskon langsung Rp10.000 untuk transaksi berikutnya.',
    pointsRequired: 28,
    rewardType: 'DISCOUNT_NOMINAL',
    rewardValue: 10000,
    isActive: true,
  },
  {
    id: 'rwd-3',
    name: 'Free Redvelvet Leton',
    description: 'Klaim 1x Cup Redvelvet Leton gratis.',
    pointsRequired: 40,
    rewardType: 'FREE_ITEM',
    rewardValue: 22000,
    isActive: true,
  },
];

/**
 * Fetch central loyalty registry from Supabase (leton_content -> loyalty_registry)
 */
async function fetchCloudLoyaltyRegistry(): Promise<LoyaltyRegistry> {
  try {
    const client = getSupabase();
    const { data, error } = await client
      .from('leton_content')
      .select('*')
      .eq('id', 'loyalty_registry')
      .maybeSingle();

    if (!error && data && data.content) {
      return {
        balances: data.content.balances || {},
        transactions: Array.isArray(data.content.transactions) ? data.content.transactions : [],
        redemptions: Array.isArray(data.content.redemptions) ? data.content.redemptions : [],
      };
    }
  } catch (err) {
    console.warn('[Supabase Loyalty Registry Fetch Error]:', err);
  }

  return {
    balances: {},
    transactions: [],
    redemptions: [],
  };
}

/**
 * Fetch all orders from Supabase (leton_content -> orders_registry)
 */
async function fetchCloudOrders(): Promise<CustomerOrder[]> {
  try {
    const client = getSupabase();
    const { data, error } = await client
      .from('leton_content')
      .select('*')
      .eq('id', 'orders_registry')
      .maybeSingle();

    if (!error && data && data.content && Array.isArray(data.content.orders)) {
      return data.content.orders;
    }
  } catch (err) {
    console.warn('[Supabase Orders Registry Fetch Error]:', err);
  }
  return [];
}

/**
 * Helper to save central loyalty registry to Supabase (leton_content -> loyalty_registry)
 */
async function saveCloudLoyaltyRegistry(registry: LoyaltyRegistry): Promise<boolean> {
  try {
    const client = getSupabase();
    const { error } = await client.from('leton_content').upsert({
      id: 'loyalty_registry',
      content: {
        balances: registry.balances || {},
        transactions: (registry.transactions || []).slice(0, 1000),
        redemptions: (registry.redemptions || []).slice(0, 1000),
      },
      updated_at: new Date().toISOString(),
    });

    if (error) {
      console.warn('[Supabase Loyalty Registry Save Error]:', error);
      return false;
    }
    return true;
  } catch (err) {
    console.warn('[Supabase Loyalty Registry Exception]:', err);
    return false;
  }
}

/**
 * 1. Fetch loyalty settings
 */
export async function getLoyaltySettings(): Promise<{ settings: LoyaltySettings; isFallback: boolean }> {
  try {
    const client = getSupabase();
    const { data, error } = await client
      .from('loyalty_settings')
      .select('*')
      .eq('id', 'default')
      .maybeSingle();

    if (!error && data) {
      return {
        settings: {
          id: data.id,
          isActive: Boolean(data.is_active),
          earningAmountPerPoint: Number(data.earning_amount_per_point || 10000),
          calculationBasis: data.calculation_basis as 'SUBTOTAL' | 'TOTAL',
          expirationMode: data.expiration_mode as 'NEVER' | 'DAYS',
          expirationDays: Number(data.expiration_days || 365),
        },
        isFallback: false,
      };
    }
  } catch (err) {
    console.warn('[Loyalty Settings Fetch Note]:', err);
  }

  return { settings: DEFAULT_SETTINGS, isFallback: false };
}

/**
 * 2. Save loyalty settings
 */
export async function saveLoyaltySettings(settings: LoyaltySettings): Promise<{ success: boolean; error?: string }> {
  try {
    const client = getSupabase();
    const payload = {
      id: 'default',
      is_active: settings.isActive,
      earning_amount_per_point: settings.earningAmountPerPoint,
      calculation_basis: settings.calculationBasis,
      expiration_mode: settings.expirationMode,
      expiration_days: settings.expirationDays,
      updated_at: new Date().toISOString(),
    };

    const { error } = await client
      .from('loyalty_settings')
      .upsert(payload, { onConflict: 'id' });

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

/**
 * 3. Fetch loyalty rewards list
 */
export async function getLoyaltyRewards(): Promise<{ rewards: LoyaltyReward[]; isFallback: boolean }> {
  try {
    const client = getSupabase();
    const { data, error } = await client
      .from('loyalty_rewards')
      .select('*')
      .order('points_required', { ascending: true });

    if (!error && data && data.length > 0) {
      const mapped: LoyaltyReward[] = data.map((r: any) => ({
        id: r.id,
        name: r.name,
        description: r.description,
        pointsRequired: Number(r.points_required),
        rewardType: r.reward_type,
        rewardValue: Number(r.reward_value),
        isActive: Boolean(r.is_active),
        redeemLimit: r.redeem_limit ? Number(r.redeem_limit) : null,
        createdAt: r.created_at,
      }));
      return { rewards: mapped, isFallback: false };
    }
  } catch (err) {
    console.warn('[Loyalty Rewards Fetch Note]:', err);
  }

  return { rewards: DEFAULT_REWARDS, isFallback: false };
}

/**
 * 4. Save loyalty reward
 */
export async function saveLoyaltyReward(reward: LoyaltyReward): Promise<{ success: boolean; error?: string }> {
  try {
    const client = getSupabase();
    const payload = {
      id: reward.id,
      name: reward.name,
      description: reward.description,
      points_required: reward.pointsRequired,
      reward_type: reward.rewardType,
      reward_value: reward.rewardValue,
      is_active: reward.isActive,
      redeem_limit: reward.redeemLimit ?? null,
      updated_at: new Date().toISOString(),
    };

    const { error } = await client
      .from('loyalty_rewards')
      .upsert(payload, { onConflict: 'id' });

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

/**
 * 5. Delete loyalty reward
 */
export async function deleteLoyaltyReward(rewardId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const client = getSupabase();
    const { error } = await client
      .from('loyalty_rewards')
      .delete()
      .eq('id', rewardId);

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

/**
 * 6. Get Customer loyalty balance directly from Cloud Supabase Database
 * Matches by customerId (UUID) or customerPhone
 */
export async function getCustomerLoyalty(customerId: string, customerPhone?: string): Promise<CustomerLoyaltyData> {
  if (!customerId && !customerPhone) {
    return { pointsBalance: 0, totalPointsEarned: 0, totalPointsRedeemed: 0 };
  }

  try {
    const client = getSupabase('super_admin');

    // 1. Primary Source: Direct query from public.customer_points (Source of Truth)
    if (customerId) {
      const { data: ptRows, error: ptErr } = await client
        .from('customer_points')
        .select('customer_id, current_balance, total_points_earned, total_points_redeemed')
        .eq('customer_id', customerId)
        .limit(1);

      if (!ptErr && ptRows && ptRows.length > 0) {
        const row = ptRows[0];
        return {
          pointsBalance: Number(row.current_balance || 0),
          totalPointsEarned: Number(row.total_points_earned || 0),
          totalPointsRedeemed: Number(row.total_points_redeemed || 0),
        };
      }
    }

    // 2. Secondary Source: Direct query from public.customers
    let query = client.from('customers').select('id, points_balance, total_points_earned, total_points_redeemed');
    if (customerId && customerPhone) {
      query = query.or(`id.eq.${customerId},nomor_hp.eq.${customerPhone}`);
    } else if (customerId) {
      query = query.eq('id', customerId);
    } else if (customerPhone) {
      query = query.eq('nomor_hp', customerPhone);
    }

    const { data: custRows, error: custErr } = await query.limit(1);

    if (!custErr && custRows && custRows.length > 0) {
      const row = custRows[0];
      return {
        pointsBalance: Number(row.points_balance || 0),
        totalPointsEarned: Number(row.total_points_earned || 0),
        totalPointsRedeemed: Number(row.total_points_redeemed || 0),
      };
    }

    // 3. Tertiary Source: Check registry.balances in loyalty_registry
    const registry = await fetchCloudLoyaltyRegistry();
    if (customerId && registry.balances && registry.balances[customerId] !== undefined) {
      const data = registry.balances[customerId];
      return {
        pointsBalance: Number(data.pointsBalance || 0),
        totalPointsEarned: Number(data.totalPointsEarned || 0),
        totalPointsRedeemed: Number(data.totalPointsRedeemed || 0),
      };
    }
  } catch (err) {
    console.warn('[Get Customer Loyalty Note]:', err);
  }

  return { pointsBalance: 0, totalPointsEarned: 0, totalPointsRedeemed: 0 };
}

/**
 * 7. Fetch Loyalty Transactions list from Cloud Supabase Database
 */
export async function getLoyaltyTransactions(customerId?: string, customerPhone?: string): Promise<PointTransaction[]> {
  try {
    const registry = await fetchCloudLoyaltyRegistry();
    let txs = [...(registry.transactions || [])];

    // Fetch directly from database table public.loyalty_transactions for complete synchronization
    try {
      const client = getSupabase('super_admin');
      let dbQuery = client.from('loyalty_transactions').select('*');
      if (customerId) {
        dbQuery = dbQuery.eq('customer_id', customerId);
      }
      const { data: dbRows, error: dbErr } = await dbQuery.order('created_at', { ascending: false }).limit(200);

      if (!dbErr && dbRows && Array.isArray(dbRows) && dbRows.length > 0) {
        const existingIds = new Set(txs.map((t) => t.id));
        for (const r of dbRows) {
          if (!existingIds.has(r.id)) {
            txs.push({
              id: r.id,
              customerId: r.customer_id,
              customerName: undefined,
              customerPhone: undefined,
              transactionType: r.transaction_type,
              points: Number(r.points),
              balanceBefore: Number(r.balance_before),
              balanceAfter: Number(r.balance_after),
              referenceOrderId: r.reference_order_id,
              referenceRewardId: r.reference_reward_id,
              reason: r.reason,
              adminUsername: r.admin_username,
              createdAt: r.created_at,
            });
            existingIds.add(r.id);
          }
        }
      }
    } catch (dbQueryErr) {
      console.warn('[getLoyaltyTransactions db query notice]:', dbQueryErr);
    }

    if (customerId || customerPhone) {
      txs = txs.filter(
        (t) =>
          (customerId && t.customerId === customerId) ||
          (customerPhone && t.customerPhone === customerPhone)
      );
    }

    return txs.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  } catch (err) {
    console.warn('[Get Loyalty Transactions Note]:', err);
    return [];
  }
}

export interface GetRewardRedemptionsOptions {
  context?: 'customer' | 'admin';
  fetchImpl?: typeof fetch;
  getCustomerToken?: () => string | null;
  getAdminToken?: () => string | null;
  getAdminRole?: () => string | null;
}

/**
 * 8. Fetch Redemptions list from Authoritative Server Endpoint (/api/loyalty/redemptions).
 * Enforces strict separation between Customer context (uses customer session token only)
 * and Admin context (uses admin session token only) without direct browser table fallbacks.
 */
export async function getRewardRedemptions(
  customerId?: string,
  options?: GetRewardRedemptionsOptions
): Promise<RewardRedemption[]> {
  try {
    const isCustomerContext =
      options?.context === 'customer' ||
      (options?.context !== 'admin' && Boolean(customerId && customerId.trim().length > 0));

    let token: string | null = null;

    if (isCustomerContext) {
      // Customer context: strictly use Customer session token; never allow stale adminToken in localStorage to override
      token = options?.getCustomerToken
        ? options.getCustomerToken()
        : getCustomerSessionToken();
    } else {
      // Admin context: check role first (Courier has no access) and strictly use Admin session token
      const rawRole = options?.getAdminRole
        ? options.getAdminRole()
        : typeof localStorage !== 'undefined'
          ? localStorage.getItem('leton_admin_role')
          : null;
      const normalizedRole = (rawRole || '').toLowerCase().trim();
      if (normalizedRole === 'courier') {
        return [];
      }
      token = options?.getAdminToken
        ? options.getAdminToken()
        : typeof localStorage !== 'undefined'
          ? localStorage.getItem('leton_admin_token')
          : null;
    }

    if (!token || !token.trim()) {
      return [];
    }

    const pathWithQuery = `/api/loyalty/redemptions${customerId ? `?customerId=${encodeURIComponent(customerId)}` : ''}`;
    const url = getApiUrl(pathWithQuery);
    const fetchFn = options?.fetchImpl || fetch;

    const resp = await fetchFn(url, {
      headers: {
        Authorization: `Bearer ${token.trim()}`,
      },
    });

    if (!resp.ok) {
      console.warn(`[getRewardRedemptions] Server rejected or failed request (HTTP ${resp.status})`);
      return [];
    }

    const resData = await resp.json().catch(() => null);
    if (resData && Array.isArray(resData.redemptions)) {
      return resData.redemptions;
    }

    return [];
  } catch (apiErr) {
    console.warn('[getRewardRedemptions API query note]:', apiErr);
    return [];
  }
}

const inFlightRedeemLock = new Set<string>();

export interface RedeemRewardResult {
  success: boolean;
  redemptionId?: string;
  pointsSpent?: number;
  pointsBalanceAfter?: number;
  redemption?: RewardRedemption;
  error?: string;
}

/**
 * 9. Core Transaction: Deduct points atomically via customer_redeem_reward RPC (Session-Validated)
 */
export async function redeemReward(
  customerId: string,
  reward: LoyaltyReward,
  customerProfile?: CustomerProfile | null
): Promise<RedeemRewardResult> {
  if (!customerId) {
    return { success: false, error: 'Customer ID tidak valid.' };
  }

  // 1. In-flight mutex lock: prevent concurrent double-click
  if (inFlightRedeemLock.has(customerId)) {
    return { success: false, error: 'Permintaan penukaran sedang diproses. Harap tunggu sejenak.' };
  }
  inFlightRedeemLock.add(customerId);

  try {
    // 2. Validate session token
    const token = getCustomerSessionToken();
    if (!token || !token.trim()) {
      return {
        success: false,
        error: 'Sesi login tidak ditemukan atau telah kedaluwarsa. Silakan login terlebih dahulu untuk menukar reward.',
      };
    }

    // 3. ATOMIC DEDUCTION & VOUCHER ISSUANCE via database RPC customer_redeem_reward
    // (Verifies token -> extracts customer_id server-side -> locks row -> checks balance -> deducts points -> creates voucher)
    const client = getSupabase();
    const { data: rpcRes, error: rpcErr } = await client.rpc('customer_redeem_reward', {
      p_token: token.trim(),
      p_reward_id: reward.id,
    });

    if (rpcErr || !rpcRes || rpcRes.success === false) {
      const errMsg = rpcRes?.error || rpcErr?.message || 'Gagal memproses penukaran reward di server.';
      return { success: false, error: errMsg };
    }

    const redemptionId = rpcRes.redemption_id || ('rdm_' + Date.now() + '_' + Math.random().toString(36).substring(2, 7));
    const pointsSpent = Number(rpcRes.points_spent ?? reward.pointsRequired ?? 0);
    const finalBalanceAfter = Number(rpcRes.points_balance ?? 0);
    const voucherCode = rpcRes.voucher_code || ('LTY-' + Math.random().toString(36).substring(2, 10).toUpperCase());

    const redemptionRecord: RewardRedemption = {
      id: redemptionId,
      customerId,
      customerName: customerProfile?.namaLengkap,
      customerPhone: customerProfile?.nomorHp,
      rewardId: rpcRes.reward_id || reward.id,
      rewardName: rpcRes.reward_name || reward.name,
      rewardType: (rpcRes.reward_type || reward.rewardType) as any,
      rewardValue: Number(rpcRes.reward_value ?? reward.rewardValue ?? 0),
      pointsSpent,
      pointsBalanceAfter: finalBalanceAfter,
      status: 'ACTIVE',
      voucherCode,
      usedAtOutletId: null,
      usedByAdmin: null,
      createdAt: rpcRes.created_at || new Date().toISOString(),
      expiresAt: rpcRes.expires_at || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    };

    // 4. Sync into loyalty_registry for reactive frontend cache
    try {
      const registry = await fetchCloudLoyaltyRegistry();
      const currentLoyalty = await getCustomerLoyalty(customerId);
      registry.redemptions = [redemptionRecord, ...(registry.redemptions || [])];
      registry.balances[customerId] = {
        pointsBalance: finalBalanceAfter,
        totalPointsEarned: currentLoyalty.totalPointsEarned,
        totalPointsRedeemed: currentLoyalty.totalPointsRedeemed + pointsSpent,
      };
      await saveCloudLoyaltyRegistry(registry);
    } catch (regErr) {
      console.warn('[saveCloudLoyaltyRegistry notice]:', regErr);
    }

    return {
      success: true,
      redemptionId,
      pointsSpent,
      pointsBalanceAfter: finalBalanceAfter,
      redemption: redemptionRecord,
    };
  } catch (err: any) {
    return { success: false, error: 'Gagal menukar reward: ' + (err?.message || err) };
  } finally {
    inFlightRedeemLock.delete(customerId);
  }
}

/**
 * 9B. Admin: Mark redemption record as USED without affecting point balance
 * Strictly enforced with verified session & atomic status update
 */
export async function markRewardRedemptionAsUsed(
  redemptionId: string,
  options?: { outletId?: string; adminUsername?: string }
): Promise<{ success: boolean; error?: string }> {
  if (!redemptionId) {
    return { success: false, error: 'ID penukaran tidak valid.' };
  }

  // 1. Primary: Use secure server endpoint with Bearer Token session verification
  try {
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('leton_admin_token') : null;
    if (token) {
      const resp = await fetch(`/api/loyalty/redemptions/${encodeURIComponent(redemptionId)}/mark-used`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          outletId: options?.outletId,
        }),
      });

      const resData = await resp.json().catch(() => null);
      if (resp.ok && resData?.success) {
        return { success: true };
      } else if (resData?.error) {
        return { success: false, error: resData.error };
      }
    }
  } catch (apiErr) {
    console.warn('[markRewardRedemptionAsUsed API proxy notice]:', apiErr);
  }

  // 2. Direct Supabase Fallback (Atomic condition: status must be ACTIVE)
  const nowIso = new Date().toISOString();
  try {
    const adminClient = getSupabase('super_admin');

    // Atomic update: only updates if status === 'ACTIVE' to prevent race condition
    const { data: updatedRows, error: dbErr } = await adminClient
      .from('reward_redemptions')
      .update({
        status: 'USED',
        redeemed_at: nowIso,
        used_at_outlet_id: options?.outletId || null,
        used_by_admin: options?.adminUsername || null,
      })
      .eq('id', redemptionId)
      .eq('status', 'ACTIVE')
      .select('*');

    if (dbErr) {
      console.warn('[markRewardRedemptionAsUsed db note]:', dbErr);
      return { success: false, error: 'Gagal memperbarui status di database: ' + dbErr.message };
    }

    if (!updatedRows || updatedRows.length === 0) {
      return { success: false, error: 'Hadiah ini sudah pernah ditandai digunakan atau tidak aktif.' };
    }

    // Sync into loyalty_registry so frontend cache stays synchronized
    try {
      const registry = await fetchCloudLoyaltyRegistry();
      if (Array.isArray(registry.redemptions)) {
        let updated = false;
        registry.redemptions = registry.redemptions.map((r) => {
          if (r.id === redemptionId) {
            updated = true;
            return {
              ...r,
              status: 'USED',
              usedAt: nowIso,
              usedAtOutletId: options?.outletId || null,
              usedByAdmin: options?.adminUsername || null,
            };
          }
          return r;
        });
        if (updated) {
          await saveCloudLoyaltyRegistry(registry);
        }
      }
    } catch (regErr) {
      console.warn('[markRewardRedemptionAsUsed registry sync note]:', regErr);
    }

    return { success: true };
  } catch (err: any) {
    return {
      success: false,
      error: 'Gagal memperbarui status penukaran: ' + (err?.message || err),
    };
  }
}

/**
 * 10. Core Transaction: Manual point adjustment by Admin
 */
export async function adjustCustomerPointsManual(
  customerId: string,
  points: number,
  type: 'MANUAL_ADD' | 'MANUAL_SUB',
  reason: string,
  adminUsername: string
): Promise<{ success: boolean; error?: string }> {
  if (!customerId) {
    return { success: false, error: 'Customer ID tidak valid.' };
  }

  try {
    const currentLoyalty = await getCustomerLoyalty(customerId);
    const adjustVal = type === 'MANUAL_ADD' ? Math.abs(points) : -Math.abs(points);
    const before = currentLoyalty.pointsBalance;
    const after = before + adjustVal;

    if (after < 0) {
      return { success: false, error: 'Pengurangan melebihi jumlah saldo poin customer saat ini.' };
    }

    const registry = await fetchCloudLoyaltyRegistry();
    registry.balances[customerId] = {
      pointsBalance: after,
      totalPointsEarned: currentLoyalty.totalPointsEarned + (adjustVal > 0 ? adjustVal : 0),
      totalPointsRedeemed: currentLoyalty.totalPointsRedeemed + (adjustVal < 0 ? Math.abs(adjustVal) : 0),
    };

    const txId = 'TX-' + Math.floor(Math.random() * 900000 + 100000);
    const tx: PointTransaction = {
      id: txId,
      customerId,
      transactionType: type,
      points: adjustVal,
      balanceBefore: before,
      balanceAfter: after,
      reason: reason || (type === 'MANUAL_ADD' ? 'Penambahan Poin Manual' : 'Pengurangan Poin Manual'),
      adminUsername,
      createdAt: new Date().toISOString(),
    };
    registry.transactions = [tx, ...(registry.transactions || [])];

    await saveCloudLoyaltyRegistry(registry);

    try {
      const adminClient = getSupabase('super_admin');
      await adminClient.from('customers').update({
        points_balance: after,
        total_points_earned: currentLoyalty.totalPointsEarned + (adjustVal > 0 ? adjustVal : 0),
        total_points_redeemed: currentLoyalty.totalPointsRedeemed + (adjustVal < 0 ? Math.abs(adjustVal) : 0),
      }).eq('id', customerId);
    } catch (dbErr) {
      console.warn('[Loyalty Adjust DB update notice]:', dbErr);
    }

    return { success: true };
  } catch (err: any) {
    return { success: false, error: 'Gagal menyesuaikan poin: ' + err.message };
  }
}

/**
 * 11. Core Operation: Automatically award points when order is placed at checkout / marked READY
 * IDEMPOTENT & SECURE: Database function process_order_points_earning computes points from database records.
 */
export async function processOrderPointsEarning(
  order: CustomerOrder
): Promise<{ success: boolean; pointsEarned?: number; error?: string }> {
  const custId = order.customerId || order.userId;
  if (!custId) {
    return { success: false, error: 'Order tidak terhubung ke customer.' };
  }

  // Do not process points for cancelled or rejected orders
  if (
    order.orderStatus === 'CANCELLED' ||
    order.orderStatus === 'REJECTED' ||
    order.paymentStatus === 'PAYMENT REJECTED' ||
    order.paymentStatus === 'REJECTED'
  ) {
    return { success: false, error: 'Pesanan dibatalkan atau ditolak.' };
  }

  try {
    // 1. Always process referral reward for eligible first-time referred orders
    try {
      await processReferralRewardIfEligible(
        custId,
        order.id,
        order.orderStatus,
        order.paymentStatus
      );
    } catch (refErr) {
      console.warn('[processReferralRewardIfEligible notice]:', refErr);
    }

    // 2. ATOMIC DATABASE POINT EARNING via process_order_points_earning RPC
    // (Calculates points strictly on server from order total_amount and settings, prevents client manipulation)
    const client = getSupabase('super_admin');
    const orderIdToProcess = order.id || order.orderNumber;
    let { data: rpcRes, error: rpcErr } = await client.rpc('process_order_points_earning', {
      p_order_id: orderIdToProcess,
    });

    let resObj: any = null;
    if (typeof rpcRes === 'string') {
      try {
        resObj = JSON.parse(rpcRes);
      } catch (jsonErr) {
        console.error('[process_order_points_earning] Malformed JSON response:', jsonErr);
        return { success: false, error: 'Respons database tidak valid.' };
      }
    } else {
      resObj = rpcRes;
    }

    // Defensive fallback: If not found and order.orderNumber exists and differs from orderIdToProcess, try order.orderNumber
    if ((rpcErr || !resObj || resObj.success === false) && order.orderNumber && order.orderNumber !== orderIdToProcess) {
      const retry = await client.rpc('process_order_points_earning', {
        p_order_id: order.orderNumber,
      });
      if (!retry.error && retry.data) {
        rpcRes = retry.data;
        rpcErr = null;
        if (typeof rpcRes === 'string') {
          try {
            resObj = JSON.parse(rpcRes);
          } catch {}
        } else {
          resObj = rpcRes;
        }
      }
    }

    if (rpcErr || !resObj || resObj.success === false) {
      const errMsg = resObj?.error || rpcErr?.message || 'Gagal memproses perolehan poin di server.';
      console.warn('[process_order_points_earning RPC notice]:', errMsg);
      return { success: false, error: errMsg };
    }

    const pointsEarned = Number(resObj.points_adjusted ?? resObj.points_earned ?? resObj.points_to_earn ?? 0);
    const finalBalance = resObj.points_balance !== undefined ? Number(resObj.points_balance) : undefined;

    // 3. Sync into loyalty_registry for reactive frontend cache
    try {
      const registry = await fetchCloudLoyaltyRegistry();
      const currentLoyalty = await getCustomerLoyalty(custId, order.customerPhone);
      const before = currentLoyalty.pointsBalance;
      const after = finalBalance !== undefined ? finalBalance : before + pointsEarned;
      const newEarned = currentLoyalty.totalPointsEarned + pointsEarned;

      registry.balances[custId] = {
        pointsBalance: after,
        totalPointsEarned: newEarned,
        totalPointsRedeemed: currentLoyalty.totalPointsRedeemed,
      };

      if (pointsEarned > 0) {
        const txId = resObj.transaction_id || ('TX-' + Math.floor(Math.random() * 900000 + 100000));
        const tx: PointTransaction = {
          id: txId,
          customerId: custId,
          customerName: order.customerName,
          customerPhone: order.customerPhone,
          transactionType: 'EARN',
          points: pointsEarned,
          balanceBefore: before,
          balanceAfter: after,
          referenceOrderId: order.id,
          reason: `Automatic Earning: Pesanan #${order.orderNumber || order.id}`,
          createdAt: new Date().toISOString(),
        };

        const alreadyRecorded = (registry.transactions || []).some((t) => t.referenceOrderId === order.id && t.transactionType === 'EARN' && !t.reason?.includes('Referral'));
        if (!alreadyRecorded) {
          registry.transactions = [tx, ...(registry.transactions || [])];
        }
      }

      await saveCloudLoyaltyRegistry(registry);
    } catch (regErr) {
      console.warn('[processOrderPointsEarning registry sync notice]:', regErr);
    }

    console.log(`[Loyalty Success]: Awarded ${pointsEarned} points for order ${order.id}.`);
    return { success: true, pointsEarned };
  } catch (err: any) {
    console.error('[Loyalty Earning Exception]:', err);
    return { success: false, error: 'Database exception: ' + err.message };
  }
}

// In-flight concurrency lock map for referral rewards
const inFlightReferralMap = new Map<string, Promise<boolean>>();

/**
 * 12. Process Member Get Member (Referral) Reward upon First Valid Order
 * Rules:
 * - Only runs once when new member (B) has referred_by set and referral_rewarded is false.
 * - Referrer (A) receives +100 points ("Referral Reward")
 * - New Member (B) receives +50 points ("Referral Reward")
 * - Strictly idempotent via database atomicity & loyalty_transactions
 */
export async function processReferralRewardIfEligible(
  customerId: string,
  orderId?: string,
  orderStatus?: string,
  paymentStatus?: string
): Promise<boolean> {
  if (!customerId) return false;

  // Never reward cancelled or rejected orders
  if (
    orderStatus === 'CANCELLED' ||
    orderStatus === 'REJECTED' ||
    paymentStatus === 'PAYMENT REJECTED' ||
    paymentStatus === 'REJECTED'
  ) {
    return false;
  }

  const lockKey = `${customerId}_${orderId || 'first_order'}`;
  if (inFlightReferralMap.has(lockKey)) {
    return inFlightReferralMap.get(lockKey)!;
  }

  const referralPromise = (async () => {
    try {
      const adminClient = getSupabase('super_admin');
      const refOrderId = orderId || 'FIRST-ORDER';

      // 1. Primary Strategy: Call canonical PostgreSQL RPC process_referral_reward
      try {
        const { data: rpcRes, error: rpcErr } = await adminClient.rpc('process_referral_reward', {
          p_order_id: refOrderId,
          p_customer_id: customerId,
        });

        if (!rpcErr && rpcRes) {
          const parsed = typeof rpcRes === 'string' ? JSON.parse(rpcRes) : rpcRes;
          const isAwarded = parsed?.awarded === true || (parsed?.success === true && parsed?.awarded);
          if (isAwarded) {
            let referrerPts = Number(parsed.referrer_points_added || parsed.referrer_points);
            let referredPts = Number(parsed.referred_points_added || parsed.referred_points);

            if (isNaN(referrerPts) || referrerPts <= 0 || isNaN(referredPts) || referredPts <= 0) {
              try {
                const { settings: currentSettings } = await getReferralRewardSettings();
                if (isNaN(referrerPts) || referrerPts <= 0) referrerPts = Number(currentSettings.referrer_reward || 100);
                if (isNaN(referredPts) || referredPts <= 0) referredPts = Number(currentSettings.referred_reward || 50);
              } catch {
                if (isNaN(referrerPts) || referrerPts <= 0) referrerPts = 100;
                if (isNaN(referredPts) || referredPts <= 0) referredPts = 50;
              }
            }

            const referrerId = parsed.referrer_id;

            console.log(`[Referral Reward RPC Success] Awarded +${referrerPts} to Referrer ${referrerId || ''} and +${referredPts} to Member ${customerId}`);
            // Sync local cloud registry for instant UI reactivity
            try {
              const registry = await fetchCloudLoyaltyRegistry();
              const nowIso = new Date().toISOString();
              
              if (referrerId) {
                const refBal = registry.balances?.[referrerId];
                const rCur = refBal?.pointsBalance || 0;
                registry.balances[referrerId] = {
                  pointsBalance: rCur + referrerPts,
                  totalPointsEarned: (refBal?.totalPointsEarned || 0) + referrerPts,
                  totalPointsRedeemed: refBal?.totalPointsRedeemed || 0,
                };
                registry.transactions.unshift({
                  id: 'TX-REF-' + Math.floor(Math.random() * 900000 + 100000),
                  customerId: referrerId,
                  transactionType: 'EARN',
                  points: referrerPts,
                  balanceBefore: rCur,
                  balanceAfter: rCur + referrerPts,
                  referenceOrderId: refOrderId,
                  reason: 'Referral Reward',
                  createdAt: nowIso,
                });
              }

              const memBal = registry.balances?.[customerId];
              const mCur = memBal?.pointsBalance || 0;
              registry.balances[customerId] = {
                pointsBalance: mCur + referredPts,
                totalPointsEarned: (memBal?.totalPointsEarned || 0) + referredPts,
                totalPointsRedeemed: memBal?.totalPointsRedeemed || 0,
              };
              registry.transactions.unshift({
                id: 'TX-REF-MEM-' + Math.floor(Math.random() * 900000 + 100000),
                customerId: customerId,
                transactionType: 'EARN',
                points: referredPts,
                balanceBefore: mCur,
                balanceAfter: mCur + referredPts,
                referenceOrderId: refOrderId,
                reason: 'Referral Reward - Referred Member',
                createdAt: nowIso,
              });

              await saveCloudLoyaltyRegistry(registry);
            } catch (syncErr) {
              console.warn('[Referral registry sync notice]:', syncErr);
            }
            return true;
          } else if (parsed?.awarded === false || (parsed?.success === false && !parsed?.awarded)) {
            console.log('[Referral Reward RPC Notice]:', parsed?.reason || 'Not eligible for referral reward');
            return false;
          }
        }
      } catch (rpcCallErr) {
        console.warn('[process_referral_reward RPC notice, running TypeScript fallback]:', rpcCallErr);
      }

      // 2. Fallback Strategy: Database checks + adjust_customer_points RPC
      const { data: memberData, error: memberErr } = await adminClient
        .from('customers')
        .select('id, nama_lengkap, nomor_hp, points_balance, total_points_earned, referred_by, referral_rewarded')
        .eq('id', customerId)
        .maybeSingle();

      if (memberErr || !memberData || !memberData.referred_by || memberData.referral_rewarded === true) {
        return false;
      }

      const referrerId = memberData.referred_by;

      // Prevent self-referral
      if (referrerId === customerId) {
        await adminClient.from('customers').update({ referral_rewarded: true }).eq('id', customerId);
        return false;
      }

      // Check referrer existence
      const { data: referrerData, error: refErr } = await adminClient
        .from('customers')
        .select('id, nama_lengkap, nomor_hp, points_balance, total_points_earned')
        .eq('id', referrerId)
        .maybeSingle();

      if (refErr || !referrerData) {
        await adminClient.from('customers').update({ referral_rewarded: true }).eq('id', customerId);
        return false;
      }

      // Atomic guard: Lock referral_rewarded = true
      const { data: updateLock, error: lockErr } = await adminClient
        .from('customers')
        .update({ referral_rewarded: true, updated_at: new Date().toISOString() })
        .eq('id', customerId)
        .eq('referral_rewarded', false)
        .select('id');

      if (lockErr || !updateLock || updateLock.length === 0) {
        // Another worker or process already rewarded this referral
        return false;
      }

      // Reward points constants from dynamic settings
      const { settings: referralSettings } = await getReferralRewardSettings();
      const REFERRER_REWARD_POINTS = Number(referralSettings.referrer_reward ?? 100);
      const MEMBER_REWARD_POINTS = Number(referralSettings.referred_reward ?? 50);

      // Award +100 to Referrer via adjust_customer_points RPC
      await adminClient.rpc('adjust_customer_points', {
        p_customer_id: referrerId,
        p_points: REFERRER_REWARD_POINTS,
        p_type: 'EARN',
        p_reason: 'Referral Reward',
        p_ref_order_id: refOrderId,
        p_admin: 'System Referral',
      });

      // Award +50 to Referred Customer via adjust_customer_points RPC on FIRST VALID TRANSACTION
      await adminClient.rpc('adjust_customer_points', {
        p_customer_id: customerId,
        p_points: MEMBER_REWARD_POINTS,
        p_type: 'EARN',
        p_reason: 'Referral Reward - Referred Member',
        p_ref_order_id: refOrderId,
        p_admin: 'System Referral',
      });

      // Update central cloud registry
      try {
        const registry = await fetchCloudLoyaltyRegistry();
        const nowIso = new Date().toISOString();

        // 1. Sync Referrer
        const refCur = registry.balances?.[referrerId]?.pointsBalance ?? Number(referrerData.points_balance || 0);
        registry.balances[referrerId] = {
          pointsBalance: refCur + REFERRER_REWARD_POINTS,
          totalPointsEarned: (registry.balances?.[referrerId]?.totalPointsEarned ?? Number(referrerData.total_points_earned || 0)) + REFERRER_REWARD_POINTS,
          totalPointsRedeemed: registry.balances?.[referrerId]?.totalPointsRedeemed || 0,
        };

        registry.transactions.unshift({
          id: 'TX-REF-' + Math.floor(Math.random() * 900000 + 100000),
          customerId: referrerId,
          customerName: referrerData.nama_lengkap,
          customerPhone: referrerData.nomor_hp,
          transactionType: 'EARN',
          points: REFERRER_REWARD_POINTS,
          balanceBefore: refCur,
          balanceAfter: refCur + REFERRER_REWARD_POINTS,
          referenceOrderId: refOrderId,
          reason: 'Referral Reward',
          createdAt: nowIso,
        });

        // 2. Sync Referred Member
        const memCur = registry.balances?.[customerId]?.pointsBalance ?? Number(memberData.points_balance || 0);
        registry.balances[customerId] = {
          pointsBalance: memCur + MEMBER_REWARD_POINTS,
          totalPointsEarned: (registry.balances?.[customerId]?.totalPointsEarned ?? Number(memberData.total_points_earned || 0)) + MEMBER_REWARD_POINTS,
          totalPointsRedeemed: registry.balances?.[customerId]?.totalPointsRedeemed || 0,
        };

        registry.transactions.unshift({
          id: 'TX-REF-MEM-' + Math.floor(Math.random() * 900000 + 100000),
          customerId: customerId,
          customerName: memberData.nama_lengkap,
          customerPhone: memberData.nomor_hp,
          transactionType: 'EARN',
          points: MEMBER_REWARD_POINTS,
          balanceBefore: memCur,
          balanceAfter: memCur + MEMBER_REWARD_POINTS,
          referenceOrderId: refOrderId,
          reason: 'Referral Reward - Referred Member',
          createdAt: nowIso,
        });

        await saveCloudLoyaltyRegistry(registry);
      } catch (regSaveErr) {
        console.warn('[Referral Registry save notice]:', regSaveErr);
      }

      console.log(`[Referral Success] Rewarded +${REFERRER_REWARD_POINTS} pts to referrer ${referrerId} for member ${customerId}`);
      return true;
    } catch (err) {
      console.error('[processReferralReward] Exception:', err);
      return false;
    }
  })();

  inFlightReferralMap.set(lockKey, referralPromise);
  try {
    return await referralPromise;
  } finally {
    inFlightReferralMap.delete(lockKey);
  }
}

/**
 * 13. Retrieve all customers with their loyalty stats (for admin dashboard)
 */
export async function getCustomersWithLoyalty(): Promise<any[]> {
  try {
    const client = getSupabase('super_admin');
    const [custRowsRes, registry] = await Promise.all([
      client.from('customers').select('id, nama_lengkap, nomor_hp, points_balance, total_points_earned, total_points_redeemed').order('nama_lengkap', { ascending: true }),
      fetchCloudLoyaltyRegistry(),
    ]);

    const custRows = custRowsRes.data || [];
    return custRows.map((c: any) => {
      const regBal = registry.balances?.[c.id];
      // public.customers is the primary source of truth; loyalty_registry is only a legacy fallback
      const bal = c.points_balance !== undefined && c.points_balance !== null
        ? Number(c.points_balance)
        : (regBal?.pointsBalance !== undefined ? Number(regBal.pointsBalance) : 0);
      const earned = c.total_points_earned !== undefined && c.total_points_earned !== null
        ? Number(c.total_points_earned)
        : (regBal?.totalPointsEarned !== undefined ? Number(regBal.totalPointsEarned) : 0);
      const redeemed = c.total_points_redeemed !== undefined && c.total_points_redeemed !== null
        ? Number(c.total_points_redeemed)
        : (regBal?.totalPointsRedeemed !== undefined ? Number(regBal.totalPointsRedeemed) : 0);

      return {
        id: c.id,
        namaLengkap: c.nama_lengkap,
        nomorHp: c.nomor_hp,
        pointsBalance: bal,
        totalPointsEarned: earned,
        totalPointsRedeemed: redeemed,
      };
    });
  } catch (err) {
    console.warn('[Get Customers With Loyalty Note]:', err);
    return [];
  }
}

// Mutex map to prevent concurrent race conditions for the same customer registration
const inFlightRegistrationBonusMap = new Map<string, Promise<{ success: boolean; pointsAwarded?: number; balance?: number; alreadyAwarded?: boolean; error?: string }>>();

/**
 * 14. Registration Bonus: Automatically awards +50 points to new members upon registration.
 * STRICTLY IDEMPOTENT:
 * - Checks if customer has already received the registration bonus in public.loyalty_transactions ("Bonus Pendaftaran Member").
 * - Checks if customer has already received it in loyalty_registry.
 * - Concurrency guard prevents duplicate processing if network retries or multiple parallel requests occur.
 * - Atomically credits points using PostgreSQL RPC adjust_customer_points (or direct atomic update fallback).
 * - Synchronizes customer_points, customers, and loyalty_transactions in database as source of truth.
 * - Synchronizes loyalty_registry in leton_content for instant reactivity.
 */
export async function awardRegistrationBonus(
  customerId: string,
  _customerName?: string,
  _customerPhone?: string
): Promise<{ success: boolean; pointsAwarded?: number; balance?: number; alreadyAwarded?: boolean; error?: string }> {
  if (!customerId) {
    return { success: false, error: 'Customer ID wajib diisi.' };
  }

  // Registration strictly awards 0 points. Referral rewards (+50 referred, +100 referrer) are given on first valid QRIS transaction.
  return {
    success: true,
    alreadyAwarded: true,
    pointsAwarded: 0,
    balance: 0,
  };
}
